import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Check, X as XIcon, Clock, FileText, Save, ListChecks, Loader2,
  Calendar, GraduationCap, ScanLine, Search
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUSES = [
  { key: "present", label: "Present", icon: Check,    color: "bg-emerald-100 text-emerald-800 border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600" },
  { key: "absent",  label: "Absent",  icon: XIcon,    color: "bg-red-100 text-red-800 border-red-200",             active: "bg-red-600 text-white border-red-600" },
  { key: "late",    label: "Late",    icon: Clock,    color: "bg-amber-100 text-amber-800 border-amber-200",       active: "bg-amber-500 text-white border-amber-500" },
  { key: "excused", label: "Excused", icon: FileText, color: "bg-slate-100 text-slate-700 border-slate-200",       active: "bg-slate-700 text-white border-slate-700" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const Attendance = () => {
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const navigate = useNavigate();
  const canMark =
    hasEffectiveCapability("attendance.mark");

  // Selectors (program → class → arm)
  const [programs, setPrograms] = useState([]);
  const [sel, setSel] = useState({ program_id: "", class_id: "", arm_id: "", date: todayIso() });
  const [search, setSearch] = useState("");

  // Roster
  const [roster, setRoster] = useState([]);
  const [meta, setMeta] = useState(null);
  const [marks, setMarks] = useState({}); // student_id → status
  const [remarks, setRemarks] = useState({}); // student_id → remark
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const program = programs.find(p => p.id === sel.program_id);
  const classes = program?.classes || [];
  const cls = classes.find(c => c.id === sel.class_id);
  const arms = cls?.arms || [];

  const loadPrograms = async () => {
    try {
      const { data } = await api.get("/programs", { params: { active_only: true } });
      const enriched = await Promise.all(data.map(async (p) => {
        const cls = (await api.get("/classes", { params: { program_id: p.id } })).data;
        return { ...p, classes: cls };
      }));
      setPrograms(enriched);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadPrograms(); }, []);

  // Cascading reset
  useEffect(() => { setSel(s => ({ ...s, class_id: "", arm_id: "" })); /* eslint-disable-next-line */ }, [sel.program_id]);
  useEffect(() => { setSel(s => ({ ...s, arm_id: "" })); /* eslint-disable-next-line */ }, [sel.class_id]);

  const loadRoster = async () => {
    if (!sel.program_id || !sel.class_id || !sel.date) return;
    setLoading(true);
    try {
      const params = { program_id: sel.program_id, class_id: sel.class_id, date: sel.date };
      if (sel.arm_id) params.arm_id = sel.arm_id;
      const { data } = await api.get("/attendance/roster", { params });
      setRoster(data.students || []);
      setMeta(data);
      const initial = {}, initRem = {};
      (data.students || []).forEach(s => { if (s.status) initial[s.student_id] = s.status; if (s.remark) initRem[s.student_id] = s.remark; });
      setMarks(initial); setRemarks(initRem);
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // auto-load when full selection ready
  useEffect(() => { if (sel.program_id && sel.class_id && sel.date) loadRoster(); /* eslint-disable-next-line */ }, [sel.program_id, sel.class_id, sel.arm_id, sel.date]);

  const filteredRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(s => s.full_name.toLowerCase().includes(q) || s.admission_no.toLowerCase().includes(q));
  }, [roster, search]);

  // counts
  const counts = useMemo(() => {
    const out = { present: 0, absent: 0, late: 0, excused: 0, unmarked: 0 };
    roster.forEach(s => {
      const m = marks[s.student_id];
      if (!m) out.unmarked++;
      else if (out[m] !== undefined) out[m]++;
    });
    return out;
  }, [roster, marks]);

  const setMark = (sid, status) => {
    setMarks(m => ({ ...m, [sid]: m[sid] === status ? null : status }));
    setDirty(true);
  };
  const setRemark = (sid, text) => { setRemarks(r => ({ ...r, [sid]: text })); setDirty(true); };

  const markAll = (status) => {
    const next = {};
    roster.forEach(s => { next[s.student_id] = status; });
    setMarks(next);
    setDirty(true);
  };
  const clearAll = () => { setMarks({}); setRemarks({}); setDirty(true); };

  const save = async () => {
    if (!canMark) return toast.error("You do not have permission to mark attendance");
    if (roster.length === 0) return toast.error("Roster is empty");
    setSaving(true);
    try {
      const records = roster.map(s => ({
        student_id: s.student_id,
        status: marks[s.student_id] || "",
        remark: remarks[s.student_id] || undefined,
      }));
      const payload = {
        date: sel.date,
        program_id: sel.program_id,
        class_id: sel.class_id,
        arm_id: sel.arm_id || undefined,
        records,
      };
      const { data } = await api.post("/attendance/bulk-mark", payload);
      toast.success(`Saved ${data.saved} marks${data.removed ? ` · cleared ${data.removed}` : ""}${data.failed_count ? ` · ${data.failed_count} failed` : ""}`);
      setDirty(false);
      await loadRoster();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="attendance-page">
      <PageHeader
        overline="Daily Roll Call"
        title="Class Attendance"
        description="Pick Program → Class → Arm → Date. Mark all present, then adjust exceptions. One click to save the entire class."
        actions={
          canMark && (
            <Button variant="outline" onClick={() => navigate("/m/scan")} data-testid="open-qr-btn"><ScanLine className="w-4 h-4 mr-1.5" /> QR Scan</Button>
          )
        }
      />

      {/* Selector Bar */}
      <Card className="border-[#E2E8F0] p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <Sel testid="att-program" label="Program" value={sel.program_id} onChange={(v) => setSel({ ...sel, program_id: v })}
            options={programs.map(p => ({ value: p.id, label: p.name }))} placeholder="Pick program" />
          <Sel testid="att-class" label="Class" value={sel.class_id} onChange={(v) => setSel({ ...sel, class_id: v })}
            options={classes.map(c => ({ value: c.id, label: c.name }))} placeholder="Pick class" disabled={!sel.program_id} />
          <Sel testid="att-arm" label="Arm (optional)" value={sel.arm_id || "all"} onChange={(v) => setSel({ ...sel, arm_id: v === "all" ? "" : v })}
            options={[{ value: "all", label: "All arms" }, ...arms.map(a => ({ value: a.id, label: a.name }))]} placeholder="All arms" disabled={!sel.class_id} />
          <div className="space-y-1.5">
            <Label className="text-xs text-[#475569]">Date</Label>
            <div className="relative">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#475569] pointer-events-none" />
              <Input type="date" value={sel.date} onChange={(e) => setSel({ ...sel, date: e.target.value })} className="h-10 pl-7 w-40 bg-white" data-testid="att-date" />
            </div>
          </div>
        </div>
        {meta && (
          <div className="mt-3 text-xs text-[#475569]">
            <GraduationCap className="inline w-3.5 h-3.5 text-[#9DCD3F] mr-1" />
            {meta.program_name} · {meta.class_name}{meta.arm_name ? ` · ${meta.arm_name}` : ""} · <strong className="text-[#1B2C5A]">{meta.total_students}</strong> student{meta.total_students !== 1 ? "s" : ""} on roster · {meta.marked_count} previously marked
          </div>
        )}
      </Card>

      {/* Summary Cards */}
      {sel.program_id && sel.class_id && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="att-summary">
          <Stat label="Present" value={counts.present} tone="emerald" />
          <Stat label="Absent"  value={counts.absent}  tone="red" />
          <Stat label="Late"    value={counts.late}    tone="amber" />
          <Stat label="Excused" value={counts.excused} tone="slate" />
          <Stat label="Unmarked" value={counts.unmarked} tone="navy" />
        </div>
      )}

      {/* Action bar */}
      {sel.program_id && sel.class_id && roster.length > 0 && canMark && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#475569] mr-1 inline-flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Quick actions:</span>
            <Button size="sm" onClick={() => markAll("present")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mark-all-present"><Check className="w-3.5 h-3.5 mr-1" /> Mark All Present</Button>
            <Button size="sm" onClick={() => markAll("absent")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="mark-all-absent"><XIcon className="w-3.5 h-3.5 mr-1" /> Mark All Absent</Button>
            <Button size="sm" onClick={clearAll} variant="ghost" className="text-[#475569]" data-testid="clear-all-marks">Clear All</Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#475569]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a student…" className="h-9 pl-8 w-56 bg-white text-sm" data-testid="att-search" />
            </div>
            <Button onClick={save} disabled={saving || !dirty} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="save-attendance">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save {dirty ? "Changes" : "(no changes)"}</>}
            </Button>
          </div>
        </div>
      )}

      {/* Roster Table */}
      <Card className="border-[#E2E8F0] overflow-hidden">
        {!sel.program_id || !sel.class_id ? (
          <div className="p-10 text-center text-[#475569]">
            <ListChecks className="w-10 h-10 mx-auto text-[#9DCD3F]" />
            <p className="mt-3 text-sm">Pick a <strong className="text-[#1B2C5A]">Program</strong>, then a <strong className="text-[#1B2C5A]">Class</strong> above to load the attendance sheet.</p>
          </div>
        ) : loading ? (
          <div className="p-10 text-center text-[#475569] inline-flex w-full items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading roster…</div>
        ) : filteredRoster.length === 0 ? (
          <div className="p-10 text-center text-[#475569]">{roster.length === 0 ? "No students enrolled in this class." : "No students match your search."}</div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {filteredRoster.map((s, idx) => {
              const current = marks[s.student_id];
              return (
                <li key={s.student_id} className="p-3 md:p-4 hover:bg-[#FDFBF7]" data-testid={`roster-${s.student_id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-[#475569] w-6 text-center">{idx + 1}</span>
                    {s.photo ? (
                      <img src={s.photo} alt={s.full_name} className="w-10 h-10 rounded-full object-cover border border-[#E2E8F0] shrink-0" loading="lazy" />
                    ) : (
                      <span className="w-10 h-10 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-xs font-medium shrink-0">
                        {(s.full_name?.[0] || "?").toUpperCase()}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1B2C5A] truncate">{s.full_name}</div>
                      <div className="text-xs text-[#475569] font-mono">{s.admission_no}</div>
                    </div>

                    {/* Status pills */}
                    <div className="flex gap-1.5 flex-wrap" data-testid={`status-buttons-${s.student_id}`}>
                      {STATUSES.map(st => {
                        const Icon = st.icon;
                        const isActive = current === st.key;
                        return (
                          <button
                            key={st.key}
                            disabled={!canMark}
                            onClick={() => setMark(s.student_id, st.key)}
                            className={"px-2.5 py-1.5 rounded-md text-xs border transition inline-flex items-center gap-1.5 " + (isActive ? st.active : st.color + " hover:opacity-80")}
                            data-testid={`mark-${st.key}-${s.student_id}`}
                            title={st.label}
                          >
                            <Icon className="w-3.5 h-3.5" /> {st.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Optional remark */}
                    {(current === "absent" || current === "late" || current === "excused") && (
                      <Input
                        placeholder="Remark (optional)"
                        value={remarks[s.student_id] || ""}
                        onChange={(e) => setRemark(s.student_id, e.target.value)}
                        className="h-8 w-44 text-xs bg-white"
                        data-testid={`remark-${s.student_id}`}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Mobile sticky save bar */}
      {dirty && canMark && roster.length > 0 && (
        <div className="md:hidden fixed bottom-4 inset-x-4 z-40 rounded-lg shadow-xl bg-[#1B2C5A] text-white p-3 flex items-center justify-between">
          <span className="text-xs">Unsaved changes</span>
          <Button onClick={save} disabled={saving} size="sm" className="bg-[#9DCD3F] text-[#1B2C5A] hover:bg-[#88BB35]" data-testid="mobile-save-attendance">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
          </Button>
        </div>
      )}
    </div>
  );
};

const Sel = ({ testid, label, value, onChange, options, placeholder, disabled }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[#475569]">{label}</Label>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-10 min-w-[180px] bg-white" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Stat = ({ label, value, tone }) => {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    navy: "bg-[#1B2C5A]/5 border-[#1B2C5A]/20 text-[#1B2C5A]",
  };
  return (
    <div className={"rounded-xl border p-3 " + (tones[tone] || tones.slate)}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-display mt-0.5">{value}</div>
    </div>
  );
};

export default Attendance;
